import { useMemo, useState } from "react";
import { feedFeatures, feedTabs, feedTopList } from "@/mocks/home";

export default function NewsFeed() {
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    let arr = feedFeatures;
    if (tab !== "all") arr = arr.filter((f) => f.catKey === tab);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      arr = arr.filter(
        (f) => f.title.toLowerCase().includes(q) || f.summary.toLowerCase().includes(q)
      );
    }
    return arr;
  }, [tab, query]);

  return (
    <section id="feed" className="scroll-mt-24 bg-[#faf5ee]">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
          <div>
            <div className="text-xs font-semibold tracking-widest text-[#c2410c] mb-2 flex items-center gap-2">
              <i className="ri-newspaper-line"></i> FEED
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-[#241c15]">
              最新瓜料 · 现场还原
            </h2>
          </div>
          <div className="relative w-full sm:w-72">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9a8772] text-sm"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索标题 / 关键词"
              className="w-full h-10 pl-10 pr-4 text-sm rounded-md border border-[#e0c9ae] bg-white text-[#241c15] focus:outline-none focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/10 transition"
            />
          </div>
        </div>

        {/* tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {feedTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-1.5 whitespace-nowrap transition-colors cursor-pointer ${
                tab === t.key
                  ? "bg-[#c2410c] text-white font-semibold"
                  : "bg-white text-[#6b5d4d] border border-[#ead9c5] hover:border-[#c2410c] hover:text-[#c2410c]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* feature cards */}
          <div className="lg:col-span-2">
            {list.length === 0 ? (
              <div className="bg-white border border-[#ead9c5] rounded-2xl p-10 text-center">
                <i className="ri-inbox-2-line text-3xl text-[#d4bfa4]"></i>
                <p className="mt-3 text-sm text-[#8a7760]">没有匹配的内容，换个关键词试试</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {list.map((f) => (
                  <article
                    key={f.id}
                    className="bg-white border border-[#ead9c5] rounded-xl overflow-hidden group hover:border-[#c9a576] transition-colors cursor-pointer flex flex-col"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={f.img}
                        alt={`${f.title} 相关新闻配图`}
                        className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute top-3 left-3 text-[11px] text-white bg-[#c2410c]/90 rounded-full px-2.5 py-1">
                        {f.cat}
                      </span>
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="font-display text-[17px] leading-snug font-bold text-[#241c15] group-hover:text-[#c2410c] transition-colors">
                        {f.title}
                      </h3>
                      <p className="text-[13px] leading-6 text-[#7a6a58] mt-2 flex-1">{f.summary}</p>
                      <div className="flex items-center gap-3 text-[11px] text-[#a08b73] mt-3 pt-3 border-t border-[#f0e5d6]">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {f.views} 阅读
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-chat-3-line"></i>
                          {f.comments.toLocaleString()} 讨论
                        </span>
                        <span className="ml-auto whitespace-nowrap">{f.time}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>

          {/* top reads sidebar */}
          <aside className="bg-white border border-[#ead9c5] rounded-2xl p-5 h-fit">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-5 rounded-full bg-[#c2410c]"></span>
              <h3 className="text-sm font-semibold text-[#241c15]">本周阅读 Top</h3>
              <i className="ri-arrow-up-line text-[#c2410c] ml-auto"></i>
            </div>
            <ol className="space-y-1">
              {feedTopList.map((t, i) => (
                <li
                  key={t.title}
                  className="flex gap-3 py-3 border-b border-[#f0e5d6] last:border-0 cursor-pointer group"
                >
                  <span
                    className={`font-number text-xl w-7 text-center shrink-0 ${
                      i < 3 ? "text-[#c2410c]" : "text-[#c4ad90]"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <span className="text-[10px] text-[#c2410c] bg-[#fdf0e2] rounded px-1.5 py-0.5 mr-1">
                      {t.cat}
                    </span>
                    <h4 className="text-[13px] leading-5 text-[#3a2f24] mt-1 group-hover:text-[#c2410c] transition-colors">
                      {t.title}
                    </h4>
                    <div className="text-[10px] text-[#a08b73] mt-1 flex items-center gap-2">
                      <span>{t.time}</span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {t.views}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
            <a
              href="#full"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#c2410c] mt-4 hover:underline cursor-pointer"
            >
              浏览事件全记录
              <i className="ri-arrow-right-line"></i>
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}